import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { createWarehouseSchema, type OrgUnitDto } from '@eombor/contracts';
import { Button, Dialog, Input } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { Combobox } from '../../../shared/ui/Combobox.js';
import { Select } from '../../../shared/ui/Select.js';
import { orgApi } from '../../org/api/org.api.js';
import { warehouseApi, type WarehouseDto } from '../api/warehouse.api.js';

/**
 * YANGI OMBOR OCHISH.
 *
 * Amalni MTU aktlanish bo'limi bajaradi (`warehouse.create`), skladchi emas:
 * ombor ochish — hujjat bilan rasmiylashtiriladigan qadam, uning ichki
 * tuzilmasini qurish esa alohida ruxsat (`warehouse.layout.edit`).
 *
 * Korxona QIDIRUV orqali tanlanadi, ochiluvchi ro'yxatdan emas: tuzilmada
 * 500 dan ortiq birlik bor va `GET /org-units` bir so'rovda ko'pi bilan 200
 * ta qaytaradi — to'liq ro'yxat oddiygina sig'maydi.
 *
 * `mtuId` bu yerda umuman so'ralmaydi. Uni backend korxonadan oladi
 * (`warehouse.service.ts`): forma yuborsa, odam o'z MTU sidagi korxonaga
 * ombor ochib, uni boshqa MTU ga yozib qo'ya olardi.
 */

const SEARCH_MIN = 2;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;

export interface NewWarehouseDialogProps {
  open: boolean;
  onClose: () => void;
  /** Ombor ochilgach ro'yxatni yangilash uchun. */
  onCreated: (warehouse: WarehouseDto) => void;
}

/** Vergul bilan terilgan o'lcham ham qabul qilinadi — klaviatura odati. */
const toNumber = (value: string): number => {
  const normalized = value.trim().replace(',', '.');
  return normalized === '' ? Number.NaN : Number(normalized);
};

/** Zod muammolarini maydon → matn jadvaliga aylantiradi (birinchisi yetarli). */
const errorsByField = (issues: readonly { path: PropertyKey[]; message: string }[]) => {
  const map = new Map<string, string>();
  for (const issue of issues) {
    const key = issue.path.join('.');
    if (!map.has(key)) map.set(key, issue.message);
  }
  return map;
};

export const NewWarehouseDialog = ({ open, onClose, onCreated }: NewWarehouseDialogProps) => {
  const [orgUnit, setOrgUnit] = useState<OrgUnitDto | null>(null);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [lengthM, setLengthM] = useState('');
  const [widthM, setWidthM] = useState('');
  const [heightM, setHeightM] = useState('');
  /* HOVLI — saqlashga ta'sir qilmaydi, ko'rinishga qiladi: 3D rejada
     devor ham, tom ham chizilmaydi. Yirik mol ko'pincha aynan shunday
     joyda turadi: estakada yonida, konteyner maydonchasida. */
  const [isOutdoor, setIsOutdoor] = useState(false);
  // Xatolar birinchi yuborishdan KEYIN chiqadi: hali terilmagan maydonni
  // qizartirish odamni shoshiltiradi, xato esa hali yo'q.
  const [submitted, setSubmitted] = useState(false);

  // Har ochilishda toza forma — oldingi omborning kodi qolib ketmasin.
  useEffect(() => {
    if (!open) return;
    setOrgUnit(null);
    setSearch('');
    setDebounced('');
    setCode('');
    setName('');
    setAddress('');
    setLengthM('');
    setWidthM('');
    setHeightM('');
    setIsOutdoor(false);
    setSubmitted(false);
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const query = debounced.length >= SEARCH_MIN ? debounced : '';

  const units = useQuery({
    queryKey: ['org', 'units', 'search', query],
    queryFn: () => orgApi.listUnits({ search: query, limit: SEARCH_LIMIT }),
    enabled: open && query !== '',
    staleTime: 60_000,
  });

  /*
   * Tanlangan korxona ro'yxatga MAJBURAN qo'shiladi: qidiruv matni o'zgargach
   * server boshqa yozuvlarni qaytaradi va tanlangan yozuv ro'yxatdan tushib
   * ketsa, maydon bo'shab qolgandek ko'rinardi.
   */
  const found = useMemo(() => {
    const items = units.data?.items ?? [];
    if (orgUnit === null || items.some((item) => item.id === orgUnit.id)) return items;
    return [orgUnit, ...items];
  }, [units.data, orgUnit]);

  const draft = useMemo(
    () => ({
      orgUnitId: orgUnit?.id ?? '',
      code: code.trim().toUpperCase(),
      name: name.trim(),
      ...(address.trim() === '' ? {} : { address: address.trim() }),
      dims: { lengthM: toNumber(lengthM), widthM: toNumber(widthM), heightM: toNumber(heightM) },
      isOutdoor,
    }),
    [orgUnit, code, name, address, lengthM, widthM, heightM, isOutdoor],
  );

  const parsed = createWarehouseSchema.safeParse(draft);
  const fieldErrors = submitted && !parsed.success ? errorsByField(parsed.error.issues) : new Map();
  const errorOf = (field: string): string | undefined => fieldErrors.get(field);

  const create = useMutation({
    mutationFn: () => {
      if (!parsed.success) throw new Error('invalid');
      return warehouseApi.create(parsed.data);
    },
    onSuccess: (warehouse) => {
      onCreated(warehouse);
      onClose();
    },
  });

  /* Korxonasi MTU ga bog'lanmagan bo'lsa backend baribir rad etadi
     (`warehouse.service.ts`). Buni OLDINDAN aytish kerak: aks holda odam
     butun formani to'ldirib, oxirida tushunarsiz xato oladi.

     MTU ning O'ZI ham ombor egasi bo'la oladi — markaziy ombor aynan shunday
     ochiladi. Bazada MTU satrining `mtuId` si o'z identifikatoriga teng
     (`resolveMtuId`), lekin turga qarab tekshirish uni denormalizatsiya
     holatidan mustaqil qiladi. */
  const orgUnitHasMtu = orgUnit !== null && (orgUnit.type === 'MTU' || orgUnit.mtuId !== null);
  const area = parsed.success ? Math.round(draft.dims.lengthM * draft.dims.widthM) : null;

  const submit = (): void => {
    setSubmitted(true);
    if (parsed.success && orgUnitHasMtu) create.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('warehouse.newTitle')}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('warehouse.newCancel')}
          </Button>
          <Button loading={create.isPending} disabled={!orgUnitHasMtu} onClick={submit}>
            <Plus size={16} aria-hidden />
            {t('warehouse.newSubmit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-text-muted max-w-prose text-sm text-pretty">{t('warehouse.newIntro')}</p>

        <Combobox
          label={t('warehouse.newOrgUnit')}
          placeholder={t('warehouse.newOrgUnitSearch')}
          hint={t('warehouse.newOrgUnitHint')}
          emptyText={t('warehouse.newOrgUnitEmpty')}
          loading={units.isFetching}
          onSearch={setSearch}
          options={found.map((unit) => ({
            value: unit.id,
            label: unit.name,
            hint: unit.shortName ?? unit.code,
          }))}
          value={orgUnit?.id ?? null}
          onChange={(id) => {
            const unit = found.find((item) => item.id === id) ?? null;
            setOrgUnit(unit);
            // Nom oldindan to'ldiriladi — 90% holatda shu qoladi, lekin
            // tahrirlanadi: bitta korxonada ikkinchi ombor ochilishi mumkin.
            if (unit !== null && name === '') {
              setName(t('warehouse.newNameSuggestion', { org: unit.name }));
            }
          }}
        />

        {orgUnit !== null && !orgUnitHasMtu ? (
          <p
            role="alert"
            className="border-warn/30 bg-warn-soft text-warn rounded-[var(--radius-sm)] border px-3 py-2 text-sm text-pretty"
          >
            {t('warehouse.newOrgUnitNoMtu')}
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('warehouse.newCode')}
            mono
            autoComplete="off"
            placeholder="OMB-..."
            hint={t('warehouse.newCodeHint')}
            value={code}
            error={errorOf('code')}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
          />
          <Input
            label={t('warehouse.newName')}
            autoComplete="off"
            value={name}
            error={errorOf('name')}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <Input
          label={t('warehouse.newAddress')}
          autoComplete="off"
          hint={t('warehouse.newAddressOptional')}
          value={address}
          error={errorOf('address')}
          onChange={(event) => setAddress(event.target.value)}
        />

        <Select
          label={t('warehouse.newKind')}
          hint={isOutdoor ? t('warehouse.newKindOutdoorHint') : undefined}
          value={isOutdoor ? 'OUTDOOR' : 'BUILDING'}
          options={[
            { value: 'BUILDING', label: t('warehouse.newKindBuilding') },
            { value: 'OUTDOOR', label: t('warehouse.newKindOutdoor') },
          ]}
          onChange={(event) => setIsOutdoor(event.target.value === 'OUTDOOR')}
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="text-text mb-1 text-sm font-medium">{t('warehouse.newDims')}</legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label={t('warehouse.newLength')}
              mono
              inputMode="decimal"
              value={lengthM}
              error={errorOf('dims.lengthM')}
              onChange={(event) => setLengthM(event.target.value)}
            />
            <Input
              label={t('warehouse.newWidth')}
              mono
              inputMode="decimal"
              value={widthM}
              error={errorOf('dims.widthM')}
              onChange={(event) => setWidthM(event.target.value)}
            />
            <Input
              label={t('warehouse.newHeight')}
              mono
              inputMode="decimal"
              value={heightM}
              error={errorOf('dims.heightM')}
              onChange={(event) => setHeightM(event.target.value)}
            />
          </div>
          {area === null ? null : (
            <p className="text-text-faint tabular text-xs">
              {t('warehouse.newArea', { area })}
            </p>
          )}
        </fieldset>

        {create.error === null || create.error === undefined ? null : (
          <p
            role="alert"
            className="border-danger/30 bg-danger-soft text-danger rounded-[var(--radius-sm)] border px-3 py-2 text-sm"
          >
            {create.error instanceof ApiClientError
              ? create.error.message
              : t('warehouse.newFailed')}
          </p>
        )}
      </div>
    </Dialog>
  );
};
